// Locale state + bilingual/multilingual field resolver.
//
// EN/FR are first-class: rows ship `${base}_en` / `${base}_fr` columns. Any
// other supported locale (ZH/ES/AR/DE/PT/JA) is served on-demand through the
// translations table via /v1/translate/entity (services/inference, Iron
// Rule #4 + #12). This module surfaces the active locale, persists it across
// reloads, and offers two pure helpers:
//
//   - localizedField(row, base, locale): returns null when only a TM lookup
//     would satisfy the locale (callers wire useTranslated to fill that).
//   - resolveColumnVariant(): tells call-sites whether the locale is row-
//     column-bound (en/fr) or on-demand (everything else).
//
// Iron Rule #4 compliant: no LLM calls here; all dynamic translation is
// routed through services/translation → services/inference.

import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import i18n from './i18n';

/* ────────────────────────────────────────────────────────────────────────
 *  Supported locales.
 *
 *  Picked for procurement reach (2026-05): EN/FR (Canada official), ZH
 *  (Mainland primes), ES (LATAM + US Hispanic vendors), AR (Gulf primes —
 *  RTL chrome work tracked separately), DE/PT (EU + Brazil A&D), JA (allied
 *  procurement). KO deferred — limited near-term ROI.
 *
 *  ZH defaults to Simplified (the dev default in services/translation).
 *  Traditional (`zh-Hant`) ships when a tenant explicitly requests it.
 *
 *  This is also the allowlist enforced by services/api at request time.
 * ──────────────────────────────────────────────────────────────────────── */

export const SUPPORTED_LOCALES = ['en', 'fr', 'zh', 'es', 'ar', 'de', 'pt', 'ja', 'he'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** Locales for which rows ship a `${base}_<lang>` column. */
export const COLUMN_LOCALES: ReadonlySet<Locale> = new Set(['en', 'fr']);

/** Human-readable labels + the language's own name for the dropdown. */
export const LOCALE_LABELS: Record<Locale, { code: string; label: string; native: string }> = {
  en: { code: 'EN', label: 'English', native: 'English' },
  fr: { code: 'FR', label: 'French', native: 'Français' },
  zh: { code: 'ZH', label: 'Chinese (Simplified)', native: '简体中文' },
  es: { code: 'ES', label: 'Spanish', native: 'Español' },
  ar: { code: 'AR', label: 'Arabic', native: 'العربية' },
  de: { code: 'DE', label: 'German', native: 'Deutsch' },
  pt: { code: 'PT', label: 'Portuguese', native: 'Português' },
  ja: { code: 'JA', label: 'Japanese', native: '日本語' },
  he: { code: 'HE', label: 'Hebrew', native: 'עברית' },
};

/** RTL locales — chrome flips for these when the global RTL pass lands.
 *  TODO(rtl): full bidi pass — flipping the rail, mirroring icons, swapping
 *  numeric alignment. Tracked as a separate workstream. For now the language
 *  is selectable; the chrome stays LTR. */
export const RTL_LOCALES: ReadonlySet<Locale> = new Set(['ar', 'he']);

const LOCALE_STORAGE_KEY = 'eid:locale';
const LOCALE_CHANGE_EVENT = 'eid:locale-change';

function isLocale(x: unknown): x is Locale {
  return typeof x === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(x);
}

function readStoredLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(raw) ? raw : 'en';
  } catch {
    return 'en';
  }
}

function writeStoredLocale(loc: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, loc);
  } catch {
    // Private mode / disabled storage — toggle still works in-session.
  }
}

/**
 * Pick the localized variant of a field from the row's column data.
 *
 *  Fallback chain (column-only — the on-demand translations row lookup
 *  is handled by useTranslated at the call site):
 *
 *    1. `${base}_${locale}` column (only meaningful for COLUMN_LOCALES)
 *    2. `${base}_fr` column
 *    3. `${base}_en` column
 *    4. `${base}` column
 *
 *  Returns null if nothing resolves. Empty / whitespace-only strings are
 *  treated as missing so renderers don't print blank cells.
 *
 *  For non-column locales (zh/es/ar/…), step 1 always misses and the
 *  caller is expected to wire useTranslated() to fetch dynamic text. The
 *  column-based fallback (fr → en → base) provides a safe synchronous
 *  default until the translation row arrives.
 */
export function localizedField<T extends Record<string, unknown>>(
  row: T,
  base: string,
  locale: Locale,
): string | null {
  const tryKey = (k: string): string | null => {
    const v = row[k];
    if (typeof v !== 'string') return null;
    const trimmed = v.trim();
    return trimmed.length > 0 ? v : null;
  };
  if (COLUMN_LOCALES.has(locale)) {
    const exact = tryKey(`${base}_${locale}`);
    if (exact) return exact;
  }
  const fr = tryKey(`${base}_fr`);
  if (fr) return fr;
  const en = tryKey(`${base}_en`);
  if (en) return en;
  return tryKey(base);
}

/**
 * True when the active locale needs an on-demand /v1/translate/entity
 * round-trip (i.e. the row doesn't ship a column for it). Call sites use
 * this to decide whether to wire useTranslated.
 */
export function needsDynamicTranslation(locale: Locale): boolean {
  return !COLUMN_LOCALES.has(locale);
}

// ─────────────────────────────────────────────────────────────────────────
// React Context — single source of truth for the active locale.

interface LocaleCtx {
  locale: Locale;
  setLocale: (next: Locale) => void;
}

const Ctx = createContext<LocaleCtx>({
  locale: 'en',
  setLocale: () => {
    /* no-op default */
  },
});

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = readStoredLocale();
    // Align i18next with the persisted locale before first paint.
    if (i18n.language !== stored) void i18n.changeLanguage(stored);
    return stored;
  });

  // Sync across tabs via storage event.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === LOCALE_STORAGE_KEY) {
        setLocaleState(isLocale(e.newValue) ? e.newValue : 'en');
      }
    }
    function onCustomEvent(e: Event) {
      const detail = (e as CustomEvent<Locale>).detail;
      if (isLocale(detail)) setLocaleState(detail);
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener(LOCALE_CHANGE_EVENT, onCustomEvent);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(LOCALE_CHANGE_EVENT, onCustomEvent);
    };
  }, []);

  function setLocale(next: Locale) {
    if (!isLocale(next)) return;
    setLocaleState(next);
    writeStoredLocale(next);
    // Single source of truth: forward to i18next so every `useTranslation()`
    // consumer re-renders with the new catalog.
    void i18n.changeLanguage(next);
    try {
      window.dispatchEvent(new CustomEvent<Locale>(LOCALE_CHANGE_EVENT, { detail: next }));
    } catch {
      /* no-op */
    }
    // Update <html lang> for screen readers + browser spellcheck.
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', next);
      // RTL chrome flip is a future task; we update `dir` so screen readers
      // and form inputs behave correctly even if the layout stays LTR.
      document.documentElement.setAttribute('dir', RTL_LOCALES.has(next) ? 'rtl' : 'ltr');
    }
  }

  return <Ctx.Provider value={{ locale, setLocale }}>{children}</Ctx.Provider>;
}

export function useLocale(): LocaleCtx {
  return useContext(Ctx);
}

// Apply the stored locale to <html lang> before React mounts. Mirrors the
// theme module's flash-prevention pattern.
export function applyInitialLocale(): void {
  if (typeof document === 'undefined') return;
  const loc = readStoredLocale();
  document.documentElement.setAttribute('lang', loc);
  document.documentElement.setAttribute('dir', RTL_LOCALES.has(loc) ? 'rtl' : 'ltr');
}
